<template>
  <div
    ref="dropdown"
    v-click-outside="closeDeviceListDropdown"
    class="flex h-16 w-full shrink cursor-pointer flex-col rounded-b-xl border border-light-1 py-1.5"
    :class="[
      { 'bg-light-2 dark:border-dark-4 dark:bg-dark-4': isDeviceListOpen },
      { 'bg-light-2 dark:border-dark-4 dark:bg-dark-4': !isDeviceListOpen },
    ]"
    @click="toggleDeviceListDropdown()"
  >
    <div class="flex flex-col px-3">
      <span class="flex flex-row justify-between text-xs">
        Geräte {{ activeInstallationPoints.length }}
        <em
          class="mt-[-4px] text-base transition transition-all"
          :class="
            isDeviceListOpen
              ? ['mdi', 'mdi-chevron-down rotate-180']
              : ['mdi', 'mdi-chevron-down']
          "
      /></span>
      <div class="flex flex-row justify-between">
        <div class="flex flex-row gap-2">
          <template v-if="isOnSiteInspection">
            <GatewayStatusBadge
              v-for="(ip, index) in activeInspectionGateways"
              :key="ip.type + index"
              :status="ip.inspectionData?.radioCheckResult"
              :is-selected="!!ip.inspectionData?.isInstallationLocation"
            />
            <BaseChip
              v-for="(tag, index) in deviceTags['ACTIVE'].filter(
                (tag) => tag.label === 'Test-SRT'
              )"
              :key="tag.label + index"
              :text="tag.label"
              :number="tag.info"
              :custom-color="tag.color"
              class="mb-1 w-full"
            />
          </template>
          <template v-else>
            <GatewayStatusBadge
              v-if="isInstallationLocation"
              :status="OverallRadioCheckStatus.COMPLETED"
              data-testId="installationLocationTag"
            />
          </template>

          <BaseChip
            v-if="inactiveInstallationPoints.length > 0"
            :icon="NOT_MOUNTED_ICON"
            border-color="#ea2020"
            custom-color="#a20101"
            :number="numberOfInactiveDevices"
          />
          <BaseChip
            v-if="unmountedDevices.length > 0"
            text="demontiert"
            border-color="#ffa451"
            custom-color="#744215"
            :number="unmountedDevices.length.toString()"
          />
          <BaseChip
            v-if="noSignalDevices.length > 0"
            :icon="NO_SIGNAL_ICON"
            type="danger"
            :number="noSignalDevices.length.toString()"
          />
          <BaseChip
            v-for="(tag, index) in deviceTags['ACTIVE'].filter(
              (tag) => tag.label === 'DMGW'
            )"
            :key="tag.label + index"
            :text="tag.label"
            :custom-color="tag.color"
          />
        </div>
      </div>
    </div>
    <div
      :style="translateX"
      :class="[
        {
          'scale-x-99 scale-y-0': !isDeviceListOpen,
          'scale-x-100 scale-y-100': isDeviceListOpen,
        },
        'absolute z-40 flex w-54 origin-top translate-y-[calc(0%)] transform flex-col justify-center rounded-b-xl border border-t-0 border-dark-4 border-light-2 bg-light-2 shadow-[0_1px_2px_0px_rgba(0,0,0,0.1)] transition transition-all dark:border-dark-4 dark:bg-dark-4',
      ]"
    >
      <div
        class="mx-3 my-2 flex flex-col gap-2"
        :class="{ 'scale-0': !isDeviceListOpen }"
      >
        <div v-if="!hasInstallationPoints" class="p-1">
          Es sind noch keine Geräte in der Nutzeinheit installiert.
        </div>

        <div v-if="noSignalDevices.length > 0">
          <em class="mdi text-light-error" :class="NO_SIGNAL_ICON"></em>
          Kein Signal
          <BaseChip
            class="mb-1 mr-2 w-full"
            custom-color="#710f0f"
            :number="`${noSignalDevices.length.toString()} / ${
              deviceTags['ACTIVE'].find((tag) => tag.label === 'SRT')?.info || 0
            }`"
            text="SRT"
          />
        </div>

        <div v-if="deviceTags['INACTIVE']?.length > 0">
          <em class="mdi mr-2 text-light-error" :class="NOT_MOUNTED_ICON"></em>
          <span>Keine Montage</span>
          <BaseChip
            v-for="(tag, index) in deviceTags['INACTIVE']"
            :key="tag + index"
            :text="tag.label"
            :number="tag.info"
            :custom-color="tag.color"
            class="mb-1 w-full"
          />
        </div>

        <div v-if="deviceTags['UNMOUNTED']?.length > 0">
          <em
            class="mdi mr-2 text-light-warning"
            :class="NOT_MOUNTED_ICON"
          ></em>
          <span>Demontiert</span>
          <BaseChip
            v-for="(tag, index) in deviceTags['UNMOUNTED']"
            :key="tag + index"
            :text="tag.label"
            :number="tag.info"
            :custom-color="tag.color"
            class="mb-1 w-full"
          />
        </div>

        <div v-if="deviceTags['ACTIVE']?.length > 0">
          <em class="mdi mdi-check-bold mr-2 text-dark-success"></em>installiert
          <BaseChip
            v-for="(tag, index) in deviceTags['ACTIVE']"
            :key="tag + index"
            :text="tag.label"
            :number="tag.info"
            :custom-color="tag.color"
            class="mb-1 mr-2 w-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { Roomgroup } from '@/models';
import { computed, inject, onMounted, ref } from 'vue';
import BaseChip from '@/components/UI/Chip/BaseChip.vue';
import { Tag } from '@/utils/canvas/drawingUtils';
import { NO_SIGNAL_ICON, NOT_MOUNTED_ICON } from '@/enums/icons';
import { useLayoutStore } from '@/store/layout/layoutStore';
import GatewayStatusBadge from '@/components/LoRaWanRadioCheck/Badges/GatewayStatusBadge.vue';
import { OverallRadioCheckStatus } from '@/enums/radiocheck';
import type { DeviceTags } from '@/composables/useBuildingOverview';

const props = defineProps<{
  roomGroup: Roomgroup;
  deviceTags?: DeviceTags;
  activeInstallationPoints: Array<any>;
  unmountedDevices: Array<any>;
  noSignalDevices: Array<any>;
  isInstallationLocation: boolean;
  activeInspectionGateways: Array<any>;
  isOnSiteInspection: boolean;
}>();

const dropdown = ref(null);
const isDeviceListOpen = ref<boolean>(false);
const scrollPositionRef = inject('scrollPositionRef', ref(null));
const scrollPosition = ref(0);

onMounted(() => {
  if (!scrollPositionRef.value) return;
  (scrollPositionRef.value as any).addEventListener(
    'scroll',
    getScrollPosition
  );
});

const getScrollPosition = () => {
  isDeviceListOpen.value = false;
  if (!scrollPositionRef.value) return;
  scrollPosition.value = (scrollPositionRef.value as any).scrollLeft;
};

const translateX = computed(() => {
  const x = Math.abs(scrollPosition.value + 2);
  return `--tw-translate-x: -${x}px !important`;
});

const toggleDeviceListDropdown = () => {
  if (!dropdown.value) return;
  (dropdown.value as any).scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });
  setTimeout(() => {
    isDeviceListOpen.value = !isDeviceListOpen.value;
  }, 300);
};

const closeDeviceListDropdown = () => {
  isDeviceListOpen.value = false;
};

const deviceTags = computed((): DeviceTags => {
  return props.deviceTags || { ACTIVE: [], INACTIVE: [], UNMOUNTED: [] };
});

const hasInstallationPoints = computed(() => {
  return (
    deviceTags.value['ACTIVE']?.length > 0 ||
    deviceTags.value['INACTIVE']?.length > 0
  );
});

const numberOfInactiveDevices = computed(() => {
  let totalOfInactiveDevices = 0;
  deviceTags.value['INACTIVE']?.forEach((tag: Tag) => {
    totalOfInactiveDevices = totalOfInactiveDevices + tag.info;
  });
  return totalOfInactiveDevices;
});

const activeInstallationPoints = computed(() => props.activeInstallationPoints);
const unmountedDevices = computed(() => props.unmountedDevices);
const noSignalDevices = computed(() => props.noSignalDevices);
const isInstallationLocation = computed(() => props.isInstallationLocation);
const activeInspectionGateways = computed(() => props.activeInspectionGateways);
const isOnSiteInspection = computed(() => props.isOnSiteInspection);

const inactiveInstallationPoints = computed(() => {
  return deviceTags.value['INACTIVE'] || [];
});
</script>
